import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Drawer from "react-modern-drawer";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import {
  IoClose,
  IoPrintOutline,
  IoCreateOutline,
  IoCardOutline,
  IoCopyOutline,
  IoTrashOutline,
} from "react-icons/io5";
import { get, post } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../../context/settings";
import DefaultButton from "../../../../../components/Buttons/Default";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import DOC_STYLES from "../../../../Sales/utils/docStyles";
import { printBillStatus } from "../../../utils/helpers";
import { printSlug } from "../../../../../utils/helpers/text";
import { AWS_S3_URL } from "../../../../../utils/constants";

const PRIMARY = "#6C63FF";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ViewDrawer({ billId, isOpen, onClose, onDelete, refreshBills }) {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen(prev => !prev);

  const getBill = async () => {
    if (!billId) return;
    try {
      const data = await get(`/purchases/bills/view/${billId}`);
      setBill(data.data ?? null);
      setCompanyData(data.company ?? null);
    } catch {
      toast.error(t("purchases.failedLoadBill"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!billId) return;
    setLoading(true);
    setBill(null);
    getBill();
  }, [billId]);

  const currency = (bill?.currency ?? "usd").toUpperCase();

  const supplierName = bill?.supplier
    ? (bill.supplier.company_name || `${bill.supplier.firstname ?? ""} ${bill.supplier.lastname ?? ""}`.trim())
    : "—";

  const handlePrint = () => window.print();
  const handleEdit  = () => { onClose(); navigate(`/purchases/bills/edit/${bill.id}`, { state: { billData: { ...bill } } }); };

  const handleRecordPayment = () => {
    onClose();
    navigate(`/purchases/payments/create`, {
      state: {
        paymentData: {
          supplier_id: bill.supplier_id,
          bill_id: bill.id,
          amount: bill.total_amount,
          currency: bill.currency ?? "usd"
        }
      }
    });
  };

  const clone = async () => {
    const data = await post(`/purchases/bills/clone/${bill.id}`);
    if (data?.success) {
      toast.success(t("purchases.billCloned"));
      onClose();
      navigate(`/purchases/bills/edit/${data.bill.id}`, { state: { billData: { ...data.bill } } });
    } else {
      toast.error(data?.message || t("purchases.failedCloneBill"));
    }
  };

  const billLabel = bill ? `${t('sales.billNumber')}${bill.bill_number || bill.id}` : t('purchases.billDetails');

  const totalPaid = bill?.payments?.reduce((sum, p) => sum + parseFloat(p.amount ?? 0), 0) ?? 0;
  const balance = parseFloat(bill?.total_amount ?? 0) - totalPaid;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      direction="right"
      size={680}
      enableOverlay
      lockScroll
      zIndex={9000}
      duration={200}
    >
      <div style={DOC_STYLES.wrap}>

        {/* ── Header ── */}
        <div style={DOC_STYLES.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
              onClick={onClose}
              onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
            >
              <IoClose size={20} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{billLabel}</span>
            {bill && printBillStatus(bill.status)}
          </div>
        </div>

        {/* ── Action Bar ── */}
        <div style={DOC_STYLES.actionBar}>
          <DefaultButton className="btn-action" size="sm" onClick={handleEdit} disabled={!bill} color="light" outline>
            <IoCreateOutline size={15} />
            {t('common.edit')}
          </DefaultButton>
          <DefaultButton className="btn-action" size="sm" onClick={handleRecordPayment} disabled={!bill} color="light" outline>
            <IoCardOutline size={15} />
            {t('purchases.recordPayment')}
          </DefaultButton>
          <DefaultButton className="btn-action" size="sm" onClick={handlePrint} disabled={!bill} color="light" outline>
            <IoPrintOutline size={15} />
            {t('common.print')}
          </DefaultButton>
          <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down" size="sm">
            <DropdownToggle outline color="light" className="btn-action">
              {t('common.more')} <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={clone} disabled={!bill}>
                <IoCopyOutline size={16} /> {t('sales.clone')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={onDelete} disabled={!bill}>
                <IoTrashOutline size={16} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>

        {/* ── Scrollable document area ── */}
        <div style={DOC_STYLES.scroll}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
              <Spinner color="secondary" />
            </div>
          )}

          {!loading && bill && (
            <div id="quote-print-area" style={DOC_STYLES.paper}>

              {/* Company + Bill Title */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                <div>
                  <img 
                    src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default} 
                    height={38} 
                    style={{ marginBottom: 10 }}
                    alt={t('settings.logo')}
                  />
                  <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                    { companyData?.legal_name || t('common.yourCompanyName') }
                  </div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                    { companyData?.street_address || t('common.companyStreetAddress') }<br />
                    { companyData?.city || t('settings.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('settings.postalCode') }<br />
                    { printSlug(companyData?.country) || t('common.country') }<br />
                    { companyData?.email || t('common.emailExample') }
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800, fontSize: 30, color: PRIMARY, letterSpacing: 3, lineHeight: 1, textTransform: "uppercase" }}>
                    {t('purchases.billTitle')}
                  </div>
                  <div style={{ color: "#888", fontSize: 14, marginTop: 6, fontWeight: 600 }}>
                    #{bill.bill_number || bill.id}
                  </div>
                </div>
              </div>

              <hr style={DOC_STYLES.divider} />

              {/* Vendor + Meta */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                <div>
                  <div style={DOC_STYLES.label}>{t('sales.vendor')}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>{supplierName}</div>
                  {bill.supplier?.address_line_1 && (
                    <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                      {bill.supplier.address_line_1}<br />
                      {[bill.supplier.city, bill.supplier.state, bill.supplier.postal_code].filter(Boolean).join(", ")}<br />
                      {bill.supplier.country}
                    </div>
                  )}
                </div>
                <div>
                  <table style={DOC_STYLES.metaTable}>
                    <tbody>
                      <tr>
                        <td style={DOC_STYLES.metaThTd}>{t('sales.billNumber')}</td>
                        <td style={DOC_STYLES.metaTd}>{bill.bill_number ?? "—"}</td>
                      </tr>
                      <tr>
                        <td style={DOC_STYLES.metaThTd}>{t('sales.dueDate')}</td>
                        <td style={DOC_STYLES.metaTd}>{formatDate(bill.due_date) || "—"}</td>
                      </tr>
                      <tr>
                        <td style={DOC_STYLES.metaThTd}>{t('common.status')}</td>
                        <td style={DOC_STYLES.metaTd}>{printBillStatus(bill.status)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Amount summary */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                <div style={DOC_STYLES.totalsBox}>
                  <div style={DOC_STYLES.totalsLine}>
                    <span>{t("common.totalAmount")}</span>
                    <span style={{ fontWeight: 500 }}>{fmt(bill.total_amount)} {currency}</span>
                  </div>
                  <div style={DOC_STYLES.totalsLine}>
                    <span>{t("common.totalPaid")}</span>
                    <span style={{ fontWeight: 500, color: "#22c55e" }}>{fmt(totalPaid)} {currency}</span>
                  </div>
                  <div style={DOC_STYLES.totalsBold}>
                    <span>{t("common.balanceDue")}</span>
                    <span style={{ color: balance > 0 ? "#ef4444" : PRIMARY }}>{fmt(balance)} {currency}</span>
                  </div>
                </div>
              </div>

              {/* Payments history */}
              {bill.payments?.length > 0 && (
                <div style={{ marginTop: 8, marginBottom: 24 }}>
                  <div style={DOC_STYLES.label}>{t('hr.paymentHistory')}</div>
                  <table style={DOC_STYLES.itemsTable}>
                    <thead>
                      <tr>
                        <th style={DOC_STYLES.th}>{t('common.date')}</th>
                        <th style={DOC_STYLES.th}>{t('common.method')}</th>
                        <th style={DOC_STYLES.th}>{t('common.reference')}</th>
                        <th style={{ ...DOC_STYLES.thRight, borderRadius: "0 6px 0 0" }}>{t('common.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bill.payments.map((p, i) => (
                        <tr key={p.id ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={DOC_STYLES.td}>{formatDate(p.payment_date) || "—"}</td>
                          <td style={DOC_STYLES.td}>{p.payment_method || "—"}</td>
                          <td style={DOC_STYLES.td}>{p.reference || "—"}</td>
                          <td style={DOC_STYLES.tdRight}>{fmt(p.amount)} {(p.currency ?? currency).toUpperCase()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Notes */}
              {bill.notes && (
                <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #eee" }}>
                  <div style={DOC_STYLES.label}>{t('common.notes')}</div>
                  <p style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7 }}>
                    {bill.notes}
                  </p>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
